import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HostTutorRosterCard from '../../components/host/HostTutorRosterCard';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import { tutorApiClient } from '../../tutorApiClient';
import { HostTutorResponse, TutorInviteResponse } from '../../models/Tutor';
import { hostApiClient } from '../../hostApiClient';
import { useSubscriptionFeaturesEnabled } from '../../utils/subscriptionFeatures';
import { authService } from '../../services/AuthService';
import {
  hostTutorDisplayName,
  normalizeHostTutorStatus,
} from '../../utils/hostTutorDisplay';
import { Space } from '../../models/Space';
import TutorProfileCompletionPage from '../tutor/TutorProfileCompletionPage';
import { hostCanManageTutors } from '../../utils/hostTutorManagement';

type StatusFilter = 'ALL' | 'ACTIVE' | 'INVITED' | 'SUSPENDED';
type SpaceFilter = 'ALL' | string;
type AddSelfStage = 'space' | 'profile';

const rosterPanelSx = {
  borderRadius: 2.5,
  border: 1,
  borderColor: 'divider',
  overflow: 'hidden',
  backgroundImage: (theme: Theme) =>
    `linear-gradient(168deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(
      theme.palette.primary.dark,
      0.16
    )} 100%)`,
};

const HostMyTutorsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const subscriptionFeaturesEnabled = useSubscriptionFeaturesEnabled();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tutorManagementAllowed, setTutorManagementAllowed] = useState(true);
  const [invite, setInvite] = useState<TutorInviteResponse | null>(null);
  const [tutors, setTutors] = useState<HostTutorResponse[]>([]);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [spaceFilter, setSpaceFilter] = useState<SpaceFilter>('ALL');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSpaceId, setInviteSpaceId] = useState('');
  const [addSelfOpen, setAddSelfOpen] = useState(false);
  const [addSelfSpaceId, setAddSelfSpaceId] = useState('');
  const [addSelfStage, setAddSelfStage] = useState<AddSelfStage>('space');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [visibilityUpdatingId, setVisibilityUpdatingId] = useState<string | null>(null);
  const [spaceUpdatingId, setSpaceUpdatingId] = useState<string | null>(null);

  const loadTutors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const organization = await hostApiClient.getOrganization();
      const allowed = hostCanManageTutors(organization.organizationType);
      setTutorManagementAllowed(allowed);
      if (!allowed) {
        setTutors([]);
        return;
      }
      const data = await tutorApiClient.getMyTutors();
      setTutors(data);
    } catch {
      setError(t('hostMyTutors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleTutorVisibilityChange = async (tutorId: string, visible: boolean) => {
    setVisibilityUpdatingId(tutorId);
    try {
      await tutorApiClient.updateHostTutorVisibility(tutorId, visible);
      setTutors((prev) =>
        prev.map((tutor) => (tutor.tutorId === tutorId ? { ...tutor, visible } : tutor))
      );
      setSnackbar({
        open: true,
        message: visible
          ? t('hostMyTutors.visibilityShown', 'Tutor is now visible in public search.')
          : t('hostMyTutors.visibilityHidden', 'Tutor is hidden from public search.'),
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: t('hostMyTutors.visibilityUpdateError', 'Failed to update tutor visibility.'),
        severity: 'error',
      });
    } finally {
      setVisibilityUpdatingId(null);
    }
  };

  const handleTutorSpaceChange = async (tutorId: string, spaceId: string) => {
    setSpaceUpdatingId(tutorId);
    try {
      await tutorApiClient.updateHostTutorSpace(tutorId, spaceId);
      await loadTutors();
      const spaceName = spaces.find((space) => space.id === spaceId)?.name;
      setSnackbar({
        open: true,
        message: spaceName
          ? t('hostMyTutors.spaceAssigned', 'Tutor moved to {{spaceName}}.', { spaceName })
          : t('hostMyTutors.spaceAssignedGeneric', 'Tutor space assignment updated.'),
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: t('hostMyTutors.spaceUpdateError', 'Failed to update tutor space assignment.'),
        severity: 'error',
      });
    } finally {
      setSpaceUpdatingId(null);
    }
  };

  useEffect(() => {
    void loadTutors();
  }, [loadTutors]);

  useEffect(() => {
    void (async () => {
      try {
        const orgSpaces = await hostApiClient.getSpaces();
        setSpaces(orgSpaces);
        if (orgSpaces.length === 1) {
          setInviteSpaceId(orgSpaces[0].id);
        }
        setAddSelfSpaceId((current) =>
          orgSpaces.some((space) => space.id === current) ? current : orgSpaces[0]?.id ?? ''
        );
      } catch {
        setSpaces([]);
      }
    })();
  }, []);

  const openInviteDialog = () => {
    setInvite(null);
    setInviteOpen(true);
    if (spaces.length === 1) {
      setInviteSpaceId(spaces[0].id);
    }
  };

  const handleCreateInvite = async () => {
    if (!spaces.length) {
      setError(t('hostMyTutors.needsSpace'));
      return;
    }
    const spaceId = inviteSpaceId || spaces[0]?.id;
    if (!spaceId) {
      setError(t('hostMyTutors.needsSpace'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await tutorApiClient.createTutorInvite({
        spaceId,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 19),
      });
      setInvite(response);
    } catch {
      setError(t('hostMyTutors.inviteCreateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSelfAsTutor = async () => {
    if (!addSelfSpaceId) {
      setError(t('hostMyTutors.needsSpace'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await tutorApiClient.addHostAsTutor(addSelfSpaceId);
      const sessionRefreshed = await authService.refreshSessionAfterProfileCompletion();
      await loadTutors();
      if (!sessionRefreshed) {
        throw new Error('Tutor session refresh failed');
      }
      setAddSelfStage('profile');
      setSnackbar({
        open: true,
        message: t('hostMyTutors.addSelfSuccess', 'You were added to the tutor roster.'),
        severity: 'success',
      });
    } catch {
      setError(t('hostMyTutors.addSelfError'));
    } finally {
      setSubmitting(false);
    }
  };

  const openAddSelfDialog = () => {
    setError(null);
    setAddSelfStage('space');
    setAddSelfSpaceId((current) =>
      spaces.some((space) => space.id === current) ? current : spaces[0]?.id ?? ''
    );
    setAddSelfOpen(true);
  };

  const closeAddSelfDialog = () => {
    if (!submitting) {
      setAddSelfOpen(false);
      setAddSelfStage('space');
    }
  };

  const openSelfTutorProfile = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const roles = authService.getRoles();
      if (!roles.includes('USER') || !roles.includes('TUTOR')) {
        const sessionRefreshed = await authService.refreshSessionAfterProfileCompletion();
        if (!sessionRefreshed) {
          throw new Error('Tutor session refresh failed');
        }
      }
      navigate('/tutor/listing');
    } catch {
      setError(t('hostMyTutors.addSelfError'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyInviteLink = async () => {
    if (!invite?.inviteLink) return;
    try {
      await navigator.clipboard.writeText(invite.inviteLink);
      setSnackbar({
        open: true,
        message: t('hostMyTutors.inviteCopied', 'Invite link copied.'),
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: t('hostMyTutors.inviteCopyError', 'Could not copy link.'),
        severity: 'error',
      });
    }
  };

  const currentUserId = authService.getUserId();

  const isSelfOnRoster = useMemo(
    () =>
      !!currentUserId &&
      tutors.some((tutor) => !!tutor.userId && tutor.userId === currentUserId),
    [currentUserId, tutors],
  );

  const statusCounts = useMemo(() => {
    const counts = { invited: 0, suspended: 0 };
    for (const tutor of tutors) {
      switch (normalizeHostTutorStatus(tutor.status)) {
        case 'INVITED':
          counts.invited += 1;
          break;
        case 'SUSPENDED':
          counts.suspended += 1;
          break;
        default:
          break;
      }
    }
    return counts;
  }, [tutors]);

  const filteredTutors = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tutors.filter((tutor) => {
      const status = normalizeHostTutorStatus(tutor.status);
      if (statusFilter !== 'ALL' && status !== statusFilter) return false;
      if (spaceFilter !== 'ALL' && tutor.spaceId !== spaceFilter) return false;
      if (!query) return true;
      const haystack = [
        hostTutorDisplayName(tutor),
        tutor.headline,
        tutor.spaceName,
        tutor.overview,
        ...(tutor.skills ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [search, spaceFilter, statusFilter, tutors]);

  const showSpaceFilters = spaces.length > 1;

  const statusFilters: { value: StatusFilter; label: string }[] = [
    { value: 'ALL', label: t('hostMyTutors.filterAll', 'All Tutors') },
    { value: 'ACTIVE', label: t('hostMyTutors.tutorStatus.ACTIVE') },
    { value: 'INVITED', label: t('hostMyTutors.tutorStatus.INVITED') },
    { value: 'SUSPENDED', label: t('hostMyTutors.tutorStatus.SUSPENDED') },
  ];

  const spaceFilters: { value: SpaceFilter; label: string }[] = [
    { value: 'ALL', label: t('hostMyTutors.filterAllSpaces', 'All Spaces') },
    ...spaces.map((space) => ({ value: space.id, label: space.name })),
  ];

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('ALL');
    setSpaceFilter('ALL');
  };

  const filterGroupSx = {
    flexWrap: 'wrap' as const,
    bgcolor: alpha('#ffffff', 0.06),
    border: 1,
    borderColor: alpha('#ffffff', 0.16),
    borderRadius: 1,
    overflow: 'hidden',
    '& .MuiToggleButtonGroup-grouped': {
      margin: 0,
      border: 0,
      borderRadius: '0 !important',
      px: 1.5,
      py: 0.75,
      minHeight: 34,
      textTransform: 'none',
      fontWeight: 800,
      fontSize: '0.8125rem',
      letterSpacing: '0.01em',
      color: 'text.secondary',
      bgcolor: 'transparent',
      '&:not(:first-of-type)': {
        borderLeft: 1,
        borderColor: alpha('#ffffff', 0.14),
        marginLeft: 0,
      },
      '&:hover': {
        bgcolor: alpha('#ffffff', 0.1),
      },
      '&.Mui-selected': {
        color: '#dce4ff',
        bgcolor: alpha('#4E67EB', 0.42),
        '&:hover': {
          bgcolor: alpha('#4E67EB', 0.52),
        },
      },
    },
  };

  if (!loading && !tutorManagementAllowed) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {t(
            'subscription.tutorsUnavailableForStudio',
            'Rehearsal studios cannot manage tutors. Switch to a music school or tutor plan to invite and assign instructors.',
          )}
        </Alert>
        {subscriptionFeaturesEnabled && (
          <Button
            component={RouterLink}
            to="/host/subscription"
            variant="contained"
            sx={{ mt: 2 }}
          >
            {t('subscription.pageTitle', 'Plan and Billing')}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 1280,
        mx: 'auto',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          pb: 2.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                border: 1,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.28),
              }}
            >
              <GroupOutlinedIcon sx={{ color: 'primary.light', fontSize: 22 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              {t('hostMyTutors.title')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, lineHeight: 1.6 }}>
            {t(
              'hostMyTutors.subtitle',
              'Your school’s teaching roster — invite instructors, track status, and see where they teach.'
            )}
          </Typography>
          {!loading && (statusCounts.invited > 0 || statusCounts.suspended > 0) ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {statusCounts.invited > 0 ? (
                <Chip
                  size="small"
                  label={t('hostMyTutors.statsInvited', '{{count}} invited', { count: statusCounts.invited })}
                  sx={{ bgcolor: alpha('#FFB648', 0.14), color: '#ffd89a', fontWeight: 700 }}
                />
              ) : null}
              {statusCounts.suspended > 0 ? (
                <Chip
                  size="small"
                  label={t('hostMyTutors.statsSuspended', '{{count}} suspended', {
                    count: statusCounts.suspended,
                  })}
                  sx={{ bgcolor: alpha('#FF4B4B', 0.12), color: '#ffb4b4', fontWeight: 700 }}
                />
              ) : null}
            </Stack>
          ) : null}
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            component={RouterLink}
            to="/host/tutor-requests"
            variant="text"
            color="inherit"
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            {t('hostMyTutors.viewBookings', 'Tutor bookings')}
          </Button>
          {!isSelfOnRoster ? (
            <Button variant="outlined" disabled={submitting} onClick={openAddSelfDialog}>
              {t('hostMyTutors.addSelf')}
            </Button>
          ) : null}
          <Button
            variant="contained"
            color="secondary"
            disabled={submitting}
            startIcon={<PersonAddAlt1OutlinedIcon />}
            onClick={openInviteDialog}
          >
            {t('hostMyTutors.inviteTutor')}
          </Button>
        </Stack>
      </Box>

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button size="small" color="inherit" onClick={loadTutors}>
              {t('common.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      <Box sx={rosterPanelSx}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.12),
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('hostMyTutors.searchPlaceholder', 'Search tutors')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { sm: 180 },
                width: { xs: '100%', sm: 180 },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha('#ffffff', 0.04),
                  borderRadius: 1.5,
                },
              }}
            />
            {showSpaceFilters ? (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={spaceFilter}
                onChange={(_event, value: SpaceFilter | null) => {
                  if (value != null) setSpaceFilter(value);
                }}
                sx={filterGroupSx}
              >
                {spaceFilters.map((filter) => (
                  <ToggleButton key={filter.value} value={filter.value}>
                    {filter.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : null}
          </Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={statusFilter}
            onChange={(_event, value: StatusFilter | null) => {
              if (value != null) setStatusFilter(value);
            }}
            sx={filterGroupSx}
          >
            {statusFilters.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          {loading ? (
            <Grid container spacing={2}>
              {[0, 1, 2].map((key) => (
                <Grid item xs={12} md={6} key={key}>
                  <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : tutors.length === 0 ? (
            <Stack alignItems="center" spacing={2} sx={{ py: { xs: 5, sm: 7 }, px: 2, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#4E67EB', 0.12),
                  border: 1,
                  borderColor: alpha('#4E67EB', 0.24),
                }}
              >
                <GroupOutlinedIcon sx={{ fontSize: 34, color: 'primary.light' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
                  {t('hostMyTutors.emptyTitle', 'Build your teaching roster')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                  {t('hostMyTutors.empty')}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {!isSelfOnRoster ? (
                  <Button variant="outlined" disabled={submitting} onClick={openAddSelfDialog}>
                    {t('hostMyTutors.addSelf')}
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={submitting}
                  startIcon={<PersonAddAlt1OutlinedIcon />}
                  onClick={openInviteDialog}
                >
                  {t('hostMyTutors.inviteTutor')}
                </Button>
              </Stack>
            </Stack>
          ) : filteredTutors.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
              <Typography variant="body1" fontWeight={600}>
                {t('hostMyTutors.noMatches', 'No tutors match your filters.')}
              </Typography>
              <Button size="small" onClick={clearFilters}>
                {t('hostMyTutors.clearFilters', 'Clear filters')}
              </Button>
            </Stack>
          ) : (
            <Grid container spacing={2}>
              {filteredTutors.map((tutor) => {
                const isSelf = !!currentUserId && !!tutor.userId && tutor.userId === currentUserId;
                return (
                  <Grid item xs={12} lg={6} key={tutor.tutorId}>
                    <HostTutorRosterCard
                      tutor={tutor}
                      isSelf={isSelf}
                      spaces={spaces}
                      onVisibilityChange={handleTutorVisibilityChange}
                      visibilityUpdating={visibilityUpdatingId === tutor.tutorId}
                      onSpaceChange={spaces.length > 0 ? handleTutorSpaceChange : undefined}
                      spaceUpdating={spaceUpdatingId === tutor.tutorId}
                      onCardClick={(tutorId) =>
                        isSelf ? void openSelfTutorProfile() : navigate(`/host/tutors/${tutorId}`)
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>

      <Dialog
        open={addSelfOpen}
        onClose={closeAddSelfDialog}
        fullWidth
        maxWidth={addSelfStage === 'profile' ? 'lg' : 'sm'}
        fullScreen={addSelfStage === 'profile' && isMobile}
        PaperProps={{
          sx: {
            maxHeight: addSelfStage === 'profile' ? { xs: '100%', md: '92vh' } : undefined,
          },
        }}
      >
        {addSelfStage === 'space' ? (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {t('hostMyTutors.addSelfDialogTitle', 'Add yourself as a tutor')}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'hostMyTutors.addSelfDialogBody',
                    'Choose your teaching space, then complete the tutor profile shown in the next step.'
                  )}
                </Typography>
                {spaces.length === 0 ? (
                  <Alert severity="warning">{t('hostMyTutors.needsSpace')}</Alert>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel id="host-self-tutor-space-label">
                      {t('hostMyTutors.addSelfSpaceLabel', 'Teaching space')}
                    </InputLabel>
                    <Select
                      labelId="host-self-tutor-space-label"
                      label={t('hostMyTutors.addSelfSpaceLabel', 'Teaching space')}
                      value={addSelfSpaceId}
                      disabled={spaces.length === 1}
                      onChange={(event) => setAddSelfSpaceId(event.target.value)}
                    >
                      {spaces.map((space) => (
                        <MenuItem key={space.id} value={space.id}>
                          {space.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={closeAddSelfDialog} disabled={submitting}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PersonAddAlt1OutlinedIcon />}
                disabled={submitting || !addSelfSpaceId}
                onClick={() => void handleAddSelfAsTutor()}
              >
                {t('hostMyTutors.addSelfContinue', 'Continue to profile')}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', py: 1, px: 1.5 }}>
              <IconButton aria-label={t('common.close', 'Close')} onClick={closeAddSelfDialog}>
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <TutorProfileCompletionPage embedded onProfileSaved={loadTutors} />
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
          {t('hostMyTutors.inviteDialogTitle', 'Invite a tutor')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t(
                'hostMyTutors.inviteDialogBody',
                'Generate a one-time link for a new instructor. They will join this organization and teach at the selected space.'
              )}
            </Typography>
            {spaces.length === 0 ? (
              <Alert severity="warning">{t('hostMyTutors.needsSpace')}</Alert>
            ) : spaces.length > 1 ? (
              <FormControl fullWidth size="small">
                <InputLabel id="host-tutor-invite-space-label">
                  {t('hostMyTutors.inviteSpaceLabel', 'Attached space')}
                </InputLabel>
                <Select
                  labelId="host-tutor-invite-space-label"
                  label={t('hostMyTutors.inviteSpaceLabel', 'Attached space')}
                  value={inviteSpaceId}
                  onChange={(event) => setInviteSpaceId(event.target.value)}
                >
                  {spaces.map((space) => (
                    <MenuItem key={space.id} value={space.id}>
                      {space.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {invite?.inviteLink ? (
              <Stack spacing={1}>
                <Typography variant="overline" color="text.secondary">
                  {t('hostMyTutors.inviteLink')}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={invite.inviteLink}
                  InputProps={{ readOnly: true }}
                />
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setInviteOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          {invite?.inviteLink ? (
            <Button
              variant="contained"
              startIcon={<ContentCopyRoundedIcon />}
              onClick={() => void copyInviteLink()}
            >
              {t('hostMyTutors.copyInvite', 'Copy link')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              disabled={submitting || spaces.length === 0}
              onClick={() => void handleCreateInvite()}
            >
              {submitting ? <CircularProgress size={22} color="inherit" /> : t('hostMyTutors.generateInvite', 'Generate link')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default HostMyTutorsPage;
